import { normalizeCaptionToneProfile } from '../../../shared/caption-tone-analysis';
import { CAPTION_TONE_SURFACE_POLICIES } from './captionToneDictionary';
export { analyzeCaptionTone, getCaptionToneAccessibilityLabel, normalizeCaptionToneProfile } from '../../../shared/caption-tone-analysis';
function getPolicy(surface) {
    return CAPTION_TONE_SURFACE_POLICIES[surface] || CAPTION_TONE_SURFACE_POLICIES.malkaVrsCc;
}
export function getCaptionPresentationMode(mode, surface) {
    const policy = getPolicy(surface);
    if (policy.allowExpressiveStyling === false)
        return 'minimal';
    const nextMode = mode || policy.defaultMode;
    if (nextMode === 'meme' && !policy.allowMeme) {
        return 'professional';
    }
    return nextMode === 'playful' ? 'comic' : nextMode;
}
export function getCaptionTonePresentation(profile, options = {}) {
    const surface = options.surface || 'malkaVrsCc';
    const policy = getPolicy(surface);
    const mode = getCaptionPresentationMode(options.mode, surface);
    // Minimal mode, certified IP CTS and the MalkaVRS / MalkaVRI in-call
    // surfaces (malkaVrsCc, vriMeeting: plain captions, Robert 2026-11-01)
    // suppress every tone channel: no colour, marker symbols, tone
    // screen-reader hints or motion. The caption text itself is untouched.
    if (mode === 'minimal' || policy.allowExpressiveStyling === false) {
        return {
            accessibilityLabel: undefined,
            className: 'caption-tone caption-tone--neutral',
            dimensionStyle: {},
            modeClassName: 'caption-mode--minimal',
            motionClassName: 'caption-motion--off',
            symbols: [],
            tone: 'neutral'
        };
    }
    const normalized = normalizeCaptionToneProfile(profile);
    const tone = normalized?.tone || 'neutral';
    const allowMotion = policy.allowMotion && Boolean(options.motionEnabled);
    const showToneLane = options.toneMarkerLane ?? policy.defaultToneMarkerLane;
    const dimensionStyle = getCaptionToneDimensionStyle(normalized, mode, policy.minimumRelativeFontScale);
    return {
        accessibilityLabel: normalized?.accessibilityLabel,
        className: getCaptionToneClassName(normalized),
        dimensionStyle,
        modeClassName: `caption-mode--${mode}`,
        motionClassName: allowMotion ? 'caption-motion--on' : 'caption-motion--off',
        style: toWebStyle(dimensionStyle),
        symbols: showToneLane && tone !== 'neutral' ? normalized?.symbols || [] : [],
        tone
    };
}
/**
 * Per-mode ceilings for the continuous controls. Professional and elegant
 * stay restrained; comic and meme may go further. Minimal never gets here.
 */
const DIMENSION_RANGES = {
    comic: { allowItalic: true, maxScale: 1.15, maxSpacingEm: 0.06, maxWeight: 800, minWeight: 400 },
    elegant: { allowItalic: true, maxScale: 1.04, maxSpacingEm: 0.03, maxWeight: 600, minWeight: 400 },
    meme: { allowItalic: true, maxScale: 1.2, maxSpacingEm: 0.08, maxWeight: 800, minWeight: 400 },
    professional: { allowItalic: false, maxScale: 1.06, maxSpacingEm: 0.03, maxWeight: 700, minWeight: 500 }
};
function roundTo(value, step) {
    return Math.round(value / step) * step;
}
/**
 * Map judged dimensions onto typography. Energy drives weight, low certainty
 * opens letter spacing, an ironic stance slants the text, and surprise adds a
 * little size. Text-only energy is damped because words carry activation
 * poorly; when audio contributed, the full range is available. Absent
 * dimensions return an empty object so class-based styling stands alone.
 */
export function getCaptionToneDimensionStyle(profile, mode, minimumRelativeFontScale = 1) {
    const dimensions = profile?.dimensions;
    if (!dimensions || mode === 'minimal')
        return {};
    const range = DIMENSION_RANGES[mode];
    const style = {};
    const audioBacked = dimensions.sources.includes('audio');
    const energy = audioBacked ? dimensions.energy : dimensions.energy * 0.75;
    style.fontWeight = roundTo(range.minWeight + (range.maxWeight - range.minWeight) * energy, 100);
    if (typeof dimensions.certainty === 'number' && dimensions.certainty < 0.6) {
        const spacing = (0.6 - dimensions.certainty) / 0.6 * range.maxSpacingEm;
        if (spacing >= 0.005)
            style.letterSpacingEm = Math.round(spacing * 1000) / 1000;
    }
    if (range.allowItalic && dimensions.socialStance === 'ironic' && dimensions.socialStanceConfidence >= 0.5) {
        style.fontStyle = 'italic';
    }
    if (typeof dimensions.surprise === 'number' && dimensions.surprise >= 0.4) {
        const scale = 1 + (dimensions.surprise - 0.4) / 0.6 * (range.maxScale - 1);
        style.fontScale = Math.max(minimumRelativeFontScale, Math.round(scale * 100) / 100);
    }
    return style;
}
function toWebStyle(dimensionStyle) {
    const style = {};
    if (dimensionStyle.fontWeight)
        style.fontWeight = dimensionStyle.fontWeight;
    if (dimensionStyle.letterSpacingEm)
        style.letterSpacing = `${dimensionStyle.letterSpacingEm}em`;
    if (dimensionStyle.fontStyle)
        style.fontStyle = dimensionStyle.fontStyle;
    if (dimensionStyle.fontScale && dimensionStyle.fontScale !== 1)
        style.fontSize = `${dimensionStyle.fontScale}em`;
    return Object.keys(style).length ? style : undefined;
}
/**
 * React Native equivalent of the web inline style. Letter spacing is in
 * points there, so the em value is scaled by the caption's base font size.
 */
export function getCaptionToneNativeStyle(dimensionStyle, baseFontSize) {
    const style = {};
    if (dimensionStyle.fontWeight)
        style.fontWeight = String(dimensionStyle.fontWeight);
    if (dimensionStyle.letterSpacingEm)
        style.letterSpacing = Math.round(dimensionStyle.letterSpacingEm * baseFontSize * 100) / 100;
    if (dimensionStyle.fontStyle)
        style.fontStyle = dimensionStyle.fontStyle;
    if (dimensionStyle.fontScale && dimensionStyle.fontScale !== 1)
        style.fontSize = Math.round(baseFontSize * dimensionStyle.fontScale * 10) / 10;
    return Object.keys(style).length ? style : undefined;
}
export function getCaptionToneClassName(profile) {
    profile = normalizeCaptionToneProfile(profile);
    if (!profile || profile.tone === 'neutral') {
        return 'caption-tone caption-tone--neutral';
    }
    return `caption-tone caption-tone--${profile.tone} caption-tone--${profile.intensity}`;
}
